import './style.scss';

import dayjs from 'dayjs';
import {
  createChart,
  CrosshairMode,
  IChartApi,
  ISeriesApi,
  PriceScaleMode,
  Time,
} from 'lightweight-charts';
import { memo, MutableRefObject, useCallback, useRef } from 'react';
import { useEffectOnce, useUpdateEffect } from 'react-use';

import { useThemeColors } from '@/shared/theme';

import { chartFormatter } from 'utils/charts';

type AreaSeries = ISeriesApi<'Area'>;
type HistogramSeries = ISeriesApi<'Histogram'>;

type Point = {
  time: Time;
  value: number;
};

type ChartSerie = {
  color: string;
  points: Point[];
  label: string;
};

type CumulativePnlChartProps = {
  series: {
    cumGrossSeries: ChartSerie[];
    cumNetSeries: ChartSerie[];
    grossSeries: ChartSerie[];
    netSeries: ChartSerie[];
    unrealisedSeries: ChartSerie[];
  };
  selectedTypes: string[];
  priceFormatter?: (num: number) => string;
};

type CreatorSeries<T> = {
  chart: IChartApi;
  series: ChartSerie[];
  ref: MutableRefObject<T[]>;
  defaultVisible?: boolean;
};

const hexColorToRgb = (hex: string) => {
  const value = parseInt(hex.replace('#', ''), 16);

  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

export const CumulativePnlChart = memo(
  ({ series, selectedTypes, priceFormatter = chartFormatter }: CumulativePnlChartProps) => {
    const palette = useThemeColors();

    const chartRef = useRef<HTMLDivElement>(null);
    const chartAPIRef = useRef<IChartApi | undefined>(undefined);

    const lineCumGrossSeriesRefs = useRef<AreaSeries[]>([]);
    const lineCumNetSeriesRefs = useRef<AreaSeries[]>([]);

    const histogramGrossSeriesRefs = useRef<HistogramSeries[]>([]);
    const histogramNetSeriesRefs = useRef<HistogramSeries[]>([]);
    const histogramUnrealisedSeriesRefs = useRef<HistogramSeries[]>([]);

    const getNormalizedPoints = (points: Point[]): Point[] =>
      points.map(point => ({
        time: dayjs.utc(point.time as number).unix() as Time,
        value: point.value,
      }));

    const createLineSeries = useCallback(({ chart, series, ref }: CreatorSeries<AreaSeries>) => {
      const chartSeriesRefs: AreaSeries[] = [];

      series.forEach(({ color, points }) => {
        const { r, g, b } = hexColorToRgb(color);
        const rgbaColor = `rgba(${r}, ${g}, ${b}, 0.2)`;

        const chartSeries = chart.addAreaSeries({
          lineWidth: 1,
          lineColor: color,
          topColor: rgbaColor,
          bottomColor: rgbaColor,
        });

        chartSeries.setData(getNormalizedPoints(points));

        chartSeriesRefs.push(chartSeries);
      });

      ref.current = chartSeriesRefs;
    }, []);

    const createHistogramSeries = useCallback(
      ({ chart, series, ref }: CreatorSeries<HistogramSeries>) => {
        const chartSeriesRefs: HistogramSeries[] = [];

        series.forEach(({ color, points }) => {
          const chartSeries = chart.addHistogramSeries({
            color,
          });

          chartSeries.setData(getNormalizedPoints(points));

          chartSeriesRefs.push(chartSeries);
        });

        ref.current = chartSeriesRefs;
      },
      [],
    );

    const removeSeries = useCallback((chart: IChartApi) => {
      lineCumGrossSeriesRefs.current.forEach(el => chart.removeSeries(el));
      lineCumGrossSeriesRefs.current = [];

      lineCumNetSeriesRefs.current.forEach(el => chart.removeSeries(el));
      lineCumNetSeriesRefs.current = [];

      histogramGrossSeriesRefs.current.forEach(el => chart.removeSeries(el));
      histogramGrossSeriesRefs.current = [];

      histogramNetSeriesRefs.current.forEach(el => chart.removeSeries(el));
      histogramNetSeriesRefs.current = [];

      histogramUnrealisedSeriesRefs.current.forEach(el => chart.removeSeries(el));
      histogramUnrealisedSeriesRefs.current = [];
    }, []);

    const renderSeries = useCallback(
      (chart: IChartApi) => {
        if (!series) return;

        if (selectedTypes.includes('gross')) {
          if (series.cumGrossSeries) {
            createLineSeries({
              ref: lineCumGrossSeriesRefs,
              series: series.cumGrossSeries,
              chart,
            });
          }

          if (series.grossSeries) {
            createHistogramSeries({
              ref: histogramGrossSeriesRefs,
              series: series.grossSeries,
              chart,
            });
          }
        }

        if (selectedTypes.includes('net')) {
          if (series.cumNetSeries) {
            createLineSeries({
              ref: lineCumNetSeriesRefs,
              series: series.cumNetSeries,
              chart,
            });
          }

          if (series.netSeries) {
            createHistogramSeries({
              ref: histogramNetSeriesRefs,
              series: series.netSeries,
              chart,
            });
          }
        }

        if (selectedTypes.includes('unrealised')) {
          if (series.unrealisedSeries) {
            createHistogramSeries({
              ref: histogramUnrealisedSeriesRefs,
              series: series.unrealisedSeries,
              chart,
            });
          }
        }
      },
      [series, selectedTypes, createLineSeries, createHistogramSeries],
    );

    const initChart = useCallback(() => {
      const chart = createChart(chartRef.current as HTMLElement, {
        autoSize: true,
        crosshair: {
          mode: CrosshairMode.Normal,
        },
        rightPriceScale: {
          mode: PriceScaleMode.Normal,
          borderColor: palette.chart_border_color,
        },
        grid: {
          horzLines: {
            color: palette.chart_line_color,
          },
          vertLines: {
            color: palette.chart_line_color,
          },
        },
        layout: {
          background: {
            color: 'transparent',
          },
          textColor: '#eee',
        },
        timeScale: {
          timeVisible: true,
          secondsVisible: false,
          borderColor: palette.chart_border_color,
          rightBarStaysOnScroll: true,
          fixLeftEdge: true,
          fixRightEdge: true,
          lockVisibleTimeRangeOnResize: true,
        },
        localization: {
          priceFormatter,
        },
      });

      chartAPIRef.current = chart;

      removeSeries(chart);
      renderSeries(chart);
    }, [removeSeries, renderSeries, priceFormatter, palette]);

    useEffectOnce(() => {
      initChart();

      if (chartAPIRef.current) {
        removeSeries(chartAPIRef.current);
        renderSeries(chartAPIRef.current);
      }

      return () => {
        if (chartAPIRef.current) {
          chartAPIRef.current.remove();
        }
      };
    });

    useUpdateEffect(() => {
      if (chartAPIRef.current) {
        removeSeries(chartAPIRef.current);
        renderSeries(chartAPIRef.current);
      }
    }, [series, selectedTypes]);

    return (
      <div className="mm-pnl-chart-container">
        <div ref={chartRef} className="chart" />
      </div>
    );
  },
);
